import { IconButton, Menu, Spotlight, SpotlightTarget, useSpotlightContext } from '@neo4j-ndl/react';
import {
  AcademicCapIconOutline,
  BookOpenIconOutline,
  QuestionMarkCircleIconOutline,
  RocketLaunchIconOutline,
  StatusIcon,
  UserGroupIconOutline,
} from '@neo4j-ndl/react/icons';
import { UI_EVENTS } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import { ONBOARDING_STEPS } from '@nx/dashboards';
import { type JSX, useRef, useState } from 'react';

import LiveChatIconOutline from '../assets/live-chat.svg';
import { useTrackUpxEvent } from '../services/segment/analytics';

type ResourceName = 'community' | 'documentation' | 'graph-academy' | 'whats-new' | 'support' | 'status';

type HelpAndLearnMenuItemTemplate = {
  icon?: JSX.Element;
  title: string;
};

type ResourceTemplate = HelpAndLearnMenuItemTemplate & { name?: ResourceName; href: string };

export type HelpAndLearnProps = {
  scope: APP_SCOPE;
};

type ResourceViewEventProperties = {
  type: 'link';
  path: APP_SCOPE;
  name: ResourceName;
};

const resources: ResourceTemplate[] = [
  {
    title: 'Documentation',
    href: 'https://neo4j.com/docs/aura/preview/',
    name: 'documentation',
    icon: <BookOpenIconOutline />,
  },
  {
    title: "What's New",
    href: 'https://neo4j-aura.canny.io/changelog',
    name: 'whats-new',
    icon: <RocketLaunchIconOutline />,
  },
  {
    title: 'GraphAcademy',
    href: 'https://graphacademy.neo4j.com/?ref=aura-help',
    name: 'graph-academy',
    icon: <AcademicCapIconOutline />,
  },
  {
    title: 'Community',
    href: 'https://dev.neo4j.com/discuss-aura',
    name: 'community',
    icon: <UserGroupIconOutline />,
  },
  {
    title: 'Support',
    href: 'https://support.neo4j.com/s/',
    name: 'support',
    icon: <img src={LiveChatIconOutline} draggable={false} />,
  },
  {
    title: 'Status',
    href: 'https://status.neo4j.io/',
    name: 'status',
    icon: <StatusIcon />,
  },
];

export const HelpAndLearnMenuItems = ({ handleClose, ...props }: HelpAndLearnProps & { handleClose?: () => void }) => {
  const trackEvent = useTrackUpxEvent();

  const close = () => {
    if (handleClose !== undefined) {
      handleClose();
    }
  };

  function trackResourceView(properties: ResourceViewEventProperties) {
    trackEvent({ event: UI_EVENTS.VIEW_RESOURCE, properties, scope: APP_SCOPE.framework });
  }

  return (
    <>
      <Menu.Items
        // Hide dividers when there are no items between them
        className="[&>.ndl-menu-divider]:hidden [&>.ndl-menu-item~.ndl-menu-divider]:block"
      >
        {resources.map((resource) => (
          <Menu.Item
            key={resource.href}
            title={resource.title}
            icon={resource.icon}
            onClick={() => {
              if (resource.name !== undefined) {
                trackResourceView({
                  type: 'link',
                  name: resource.name,
                  path: props.scope,
                });
              }
              window.open(resource.href, '_blank');
              close();
            }}
          />
        ))}
      </Menu.Items>
    </>
  );
};

export function HelpAndLearnButton(props: HelpAndLearnProps) {
  const trackEvent = useTrackUpxEvent();
  const [open, setOpen] = useState(false);
  const anchorEl = useRef<HTMLButtonElement>(null);
  const { setActiveSpotlight, setIsOpen } = useSpotlightContext();

  function handleClose() {
    setOpen(false);
  }

  function handleToggle() {
    setOpen((state) => !state);
    trackEvent({ event: UI_EVENTS.VIEW_RESOURCES, scope: APP_SCOPE.framework });
  }

  const {
    header: firstDashboardStepHeader,
    body: firstDashboardStepBody,
    customNextLabel: firstDashboardStepLabel,
  } = ONBOARDING_STEPS[5]!;

  return (
    <>
      <SpotlightTarget id="first-dashboard-step-6" indicatorVariant="point" indicatorPlacement="bottom-right">
        <IconButton
          ref={anchorEl}
          isActive={open}
          isClean
          ariaLabel="Help & learn"
          onClick={handleToggle}
          htmlAttributes={{
            title: 'Help & learn',
          }}
        >
          <QuestionMarkCircleIconOutline />
        </IconButton>
      </SpotlightTarget>
      <Menu anchorRef={anchorEl} isOpen={open} onClose={handleClose} placement="bottom-end-top-end">
        <HelpAndLearnMenuItems {...props} handleClose={handleClose} />
      </Menu>

      <Spotlight
        target={'first-dashboard-step-6'}
        beforeActions="6/6"
        actions={[
          {
            label: firstDashboardStepLabel ?? 'Got it',
            onClick: () => {
              setIsOpen(false);
              setActiveSpotlight(null);
            },
          },
        ]}
      >
        <Spotlight.Header>{firstDashboardStepHeader}</Spotlight.Header>
        <Spotlight.Body>{firstDashboardStepBody}</Spotlight.Body>
      </Spotlight>
    </>
  );
}
